import { useMemo, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DataTable,
  DiffViewer,
  EmptyState,
  ErrorBanner,
  IconButton,
  MetricStrip,
  Modal,
  PageHeader,
  Pill,
  Skeleton,
  StatusPill,
  Surface,
  TableFilterBar,
  Tooltip,
  WorkbenchPanel,
  useToast,
  type DataTableColumn,
  type TableFilterValues,
} from '@frm/ui'

import { createChatSession } from '../api/chat'
import { ApiError } from '../api/client'
import {
  fetchControlPlaneRegistry,
  type ControlPlaneChannel,
  type ControlPlaneRegistryListDto,
  type ControlPlaneRegistryRecordDto,
  type ControlPlaneStatus,
} from '../api/controlPlane'
import {
  abortSession,
  confirmSessionTransfer,
  decideSessionPermission,
  diffSessionTransfer,
  fetchSessionTransferTargets,
  fetchSessions,
  type OwnerSessionDto,
  type PermissionDecision,
  type SessionStatus,
  type SessionTransferTarget,
} from '../api/sessions'
import { formatAge, formatInteger, MONO_NUMERIC_STYLE } from '../shared/format'

type ChannelFilter = 'all' | OwnerSessionDto['channel']
type StatusFilter = 'all' | SessionStatus | 'permission'
type ViewStatus = SessionStatus | 'permission'
type RegistryChannelFilter = 'all' | ControlPlaneChannel
type RegistryStatusFilter = 'all' | ControlPlaneStatus
type RegistryProjectFilter = 'all' | string

// Store-backed backend flow is available: `/api/sessions` exposes live pending
// permissions and `/api/sessions/:id/permission` applies allow/deny per web-run.
const PERMISSION_FLOW_ENABLED = true

const PAGE_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  minWidth: 0,
}

const PANEL_STYLE: CSSProperties = {
  boxShadow: 'var(--frm-workbench-shadow-panel)',
}

const STACK_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
}

const TOOLBAR_STYLE: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 14,
  justifyContent: 'space-between',
}

const TABLE_GRID = '104px 136px minmax(190px,1fr) minmax(142px,.75fr) 140px 124px 98px 104px 206px'

const TABLE_HEAD_STYLE: CSSProperties = {
  background: 'var(--frm-workbench-well-2, #080c16)',
  borderBottom: '1px solid var(--frm-line-subtle)',
  color: 'var(--frm-text-muted)',
  display: 'grid',
  fontSize: 10,
  fontWeight: 800,
  gap: 14,
  gridTemplateColumns: TABLE_GRID,
  letterSpacing: '.08em',
  padding: '13px 18px',
  textTransform: 'uppercase',
}

const ROW_STYLE: CSSProperties = {
  alignItems: 'flex-start',
  borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
  display: 'grid',
  gap: 14,
  gridTemplateColumns: TABLE_GRID,
  padding: '15px 18px',
}

const MUTED_STYLE: CSSProperties = {
  color: 'var(--frm-text-muted)',
}

const WELL_STYLE: CSSProperties = {
  background: 'var(--frm-workbench-well, #0b1020)',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 14,
}

const CHANNEL_OPTIONS = [
  { value: 'telegram', label: 'Telegram' },
  { value: 'web', label: 'Web' },
]

const STATUS_OPTIONS = [
  { value: 'running', label: 'Running' },
  { value: 'idle', label: 'Idle' },
  ...(PERMISSION_FLOW_ENABLED ? [{ value: 'permission', label: 'Ждут прав' }] : []),
  { value: 'aborted', label: 'Aborted' },
  { value: 'error', label: 'Error' },
]

const REGISTRY_CHANNELS: ControlPlaneChannel[] = ['telegram', 'web', 'cursor-cli', 'codex-desktop', 'manual']
const REGISTRY_STATUSES: ControlPlaneStatus[] = [
  'running',
  'idle',
  'waiting_permission',
  'blocked',
  'aborted',
  'error',
  'stale',
  'closed',
]

const PROJECT_ABBR: Record<string, string> = {
  'frm.ai/ai.agents': 'АИ',
  'frm.ai/ai.market': 'МК',
  'frm.ai/ai.metrics': 'МТ',
  'frm.ai/ai.launcher': 'AI',
  'frm.ai/ui': 'UI',
}

const PROJECT_GRADIENTS = [
  ['#38BDF8', '#6366F1'],
  ['#6366F1', '#8B5CF6'],
  ['#34D399', '#0EA5E9'],
  ['#0EA5E9', '#0369A1'],
  ['#FBBF24', '#F59E0B'],
]

export function SessionsPage() {
  const [registryChannel, setRegistryChannel] = useState<RegistryChannelFilter>('all')
  const [registryStatus, setRegistryStatus] = useState<RegistryStatusFilter>('all')
  const [registryProject, setRegistryProject] = useState<RegistryProjectFilter>('all')
  const [channel, setChannel] = useState<ChannelFilter>('all')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [contextSessionId, setContextSessionId] = useState<string | null>(null)
  const [transferSessionId, setTransferSessionId] = useState<string | null>(null)
  const [targetProjectId, setTargetProjectId] = useState('')
  const [diffReadyFor, setDiffReadyFor] = useState<{ sessionId: string; targetProjectId: string } | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const registryQuery = useQuery({
    queryKey: ['control-plane', 'registry'],
    queryFn: fetchControlPlaneRegistry,
    refetchInterval: 30_000,
  })

  const sessionsQuery = useQuery({
    queryKey: ['sessions'],
    queryFn: fetchSessions,
    refetchInterval: 30_000,
  })

  const sessions = sessionsQuery.data ?? []
  const registryRecords = registryQuery.data?.records ?? []
  const visibleRegistryRecords = useMemo(
    () => registryRecords.filter((record) => matchesRegistryFilters(record, registryChannel, registryStatus, registryProject)),
    [registryChannel, registryProject, registryRecords, registryStatus],
  )
  const registryFilterValues = useMemo<TableFilterValues>(
    () => ({
      channel: registryChannel === 'all' ? undefined : registryChannel,
      status: registryStatus === 'all' ? undefined : registryStatus,
      project: registryProject === 'all' ? undefined : registryProject,
    }),
    [registryChannel, registryProject, registryStatus],
  )
  const registrySummary = useMemo(() => summarizeRegistry(registryRecords), [registryRecords])
  const visibleSessions = useMemo(
    () => sessions.filter((session) => matchesFilters(session, channel, status)),
    [channel, sessions, status],
  )
  const filterValues = useMemo(
    () => ({
      channel: channel === 'all' ? undefined : channel,
      status: status === 'all' ? undefined : status,
    }),
    [channel, status],
  )
  const pendingSessions = PERMISSION_FLOW_ENABLED
    ? visibleSessions.filter((session) => Boolean(session.permission))
    : []
  const transferSession = transferSessionId
    ? sessions.find((session) => session.id === transferSessionId) ?? null
    : null

  const targetsQuery = useQuery({
    queryKey: ['sessions', transferSessionId, 'transfer-targets'],
    queryFn: () => fetchSessionTransferTargets(transferSessionId ?? ''),
    enabled: Boolean(transferSessionId),
  })

  const handleMutationError = (title: string, error: unknown) => {
    if (error instanceof ApiError && error.status === 501) {
      toast({
        title: `${title}: в разработке`,
        description: error.message,
        tone: 'info',
        duration: 7000,
      })
      return
    }
    toast({
      title,
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
      duration: 7000,
    })
  }

  const abortMutation = useMutation({
    mutationFn: abortSession,
    onSuccess: (result) => {
      toast({
        title: 'Сессия прервана',
        description: result.message ?? 'Abort flow выполнен backend-ом.',
        tone: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
    onError: (error) => handleMutationError('Прервать сессию', error),
  })

  const permissionMutation = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: PermissionDecision }) =>
      decideSessionPermission(id, decision),
    onSuccess: (result, variables) => {
      toast({
        title: variables.decision === 'allow' ? 'Право разрешено' : 'В праве отказано',
        description: result.message ?? 'Решение передано backend-у.',
        tone: variables.decision === 'allow' ? 'success' : 'warning',
      })
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
    onError: (error) => handleMutationError('Решение по правам', error),
  })

  const transferDiffMutation = useMutation({
    mutationFn: ({ id, targetProjectId: target }: { id: string; targetProjectId: string }) =>
      diffSessionTransfer(id, target),
    onSuccess: (result, variables) => {
      setDiffReadyFor({ sessionId: variables.id, targetProjectId: variables.targetProjectId })
      toast({
        title: 'Diff переноса готов',
        description: result.message ?? 'Backend подтвердил diff переноса.',
        tone: 'success',
      })
    },
    onError: (error) => handleMutationError('Diff переноса', error),
  })

  const transferConfirmMutation = useMutation({
    mutationFn: ({ id, targetProjectId: target }: { id: string; targetProjectId: string }) =>
      confirmSessionTransfer(id, target),
    onSuccess: (result) => {
      toast({
        title: 'Сессия перенесена',
        description: result.message ?? 'cwd обновлён, сессия перезапускается backend-ом.',
        tone: 'success',
      })
      setTransferSessionId(null)
      setTargetProjectId('')
      setDiffReadyFor(null)
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
    onError: (error) => handleMutationError('Подтверждение переноса', error),
  })

  const openWebChatMutation = useMutation({
    mutationFn: async (session: OwnerSessionDto): Promise<{ id: string; agent: string; created: boolean }> => {
      if (session.channel === 'web') {
        return { id: session.id, agent: session.agent, created: false }
      }
      const chatSession = await createChatSession({
        agent: session.agent,
        project: session.project || undefined,
        sprint: session.sprint ?? null,
        mode: 'chat',
      })
      return { id: chatSession.id, agent: chatSession.agent, created: true }
    },
    onSuccess: (chatSession) => {
      navigate(`/chat/${encodeURIComponent(chatSession.agent)}?session=${encodeURIComponent(chatSession.id)}`)
      toast({
        title: chatSession.created ? 'Web-чат создан' : 'Web-чат открыт',
        description: `${chatSession.agent} · ${shortSessionId(chatSession.id)}`,
        tone: 'success',
      })
    },
    onError: (error) => toast({
      title: 'Web-чат не открыт',
      description: error instanceof Error ? error.message : 'unknown error',
      tone: 'danger',
      duration: 7000,
    }),
  })

  const openTransfer = (session: OwnerSessionDto) => {
    setTransferSessionId(session.id)
    setTargetProjectId('')
    setDiffReadyFor(null)
  }

  const closeTransfer = () => {
    setTransferSessionId(null)
    setTargetProjectId('')
    setDiffReadyFor(null)
  }

  const lastUpdated = registryQuery.data?.updatedAt ?? sessions[0]?.lastActivityAt
  const isLoading = sessionsQuery.isLoading
  const isError = sessionsQuery.isError

  return (
    <div style={PAGE_STYLE}>
      <WorkbenchPanel style={PANEL_STYLE}>
        <PageHeader
          dense
          eyebrow="Раздел · Control-plane"
          title="Реестр агентов и сессий"
          description={`read-only registry · telegram + web + cursor + desktop · обновлено ${lastUpdated ? formatDateTime(lastUpdated) : '...'}`}
          actions={
            <Button
              variant="secondary"
              leftIcon={<Icon name="refresh" spinning={registryQuery.isFetching || sessionsQuery.isFetching} />}
              onClick={() => {
                void registryQuery.refetch()
                void sessionsQuery.refetch()
              }}
            >
              Обновить реестр
            </Button>
          }
        />
        <MetricStrip
          className="sessions-metric-strip"
          minColumnWidth={170}
          items={[
            { id: 'total', label: 'всего records', value: formatInteger(registrySummary.total), tone: 'info' },
            { id: 'running', label: 'running', value: formatInteger(registrySummary.running), tone: registrySummary.running ? 'success' : 'neutral' },
            { id: 'blocked', label: 'blocked / waiting', value: formatInteger(registrySummary.blocked), tone: registrySummary.blocked ? 'warning' : 'neutral' },
            { id: 'cli', label: 'cursor / desktop / manual', value: formatInteger(registrySummary.cli), tone: registrySummary.cli ? 'info' : 'neutral' },
            { id: 'projects', label: 'проектов', value: formatInteger(registrySummary.projects), tone: 'neutral' },
          ]}
          style={{ marginTop: 20 }}
        />
      </WorkbenchPanel>

      <RegistryReadModelPanel
        dto={registryQuery.data}
        records={visibleRegistryRecords}
        filterValues={registryFilterValues}
        isLoading={registryQuery.isLoading}
        isError={registryQuery.isError}
        error={registryQuery.error}
        isFetching={registryQuery.isFetching}
        onRefetch={() => void registryQuery.refetch()}
        onFilterChange={(values) => {
          setRegistryChannel(parseRegistryChannelFilter(values.channel))
          setRegistryStatus(parseRegistryStatusFilter(values.status))
          setRegistryProject(parseRegistryProjectFilter(values.project))
        }}
        onResetFilters={() => {
          setRegistryChannel('all')
          setRegistryStatus('all')
          setRegistryProject('all')
        }}
      />

      {isLoading ? (
        <SessionsSkeleton />
      ) : isError ? (
        <ErrorBanner
          title="Не удалось загрузить реестр сессий"
          description={sessionsQuery.error instanceof Error ? sessionsQuery.error.message : 'unknown error'}
          action={<Button onClick={() => void sessionsQuery.refetch()}>Повторить</Button>}
        />
      ) : (
        <>
          {pendingSessions.length > 0 && (
            <PendingPermissions
              sessions={pendingSessions}
              pendingMutationId={permissionMutation.variables?.id}
              pendingDecision={permissionMutation.variables?.decision}
              isPending={permissionMutation.isPending}
              onDecision={(id, decision) => permissionMutation.mutate({ id, decision })}
            />
          )}

          <WorkbenchPanel style={PANEL_STYLE}>
            <div style={TOOLBAR_STYLE}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <h2 style={{ fontSize: 16, fontWeight: 850, margin: 0 }}>Активные сессии</h2>
                <span style={{ ...MUTED_STYLE, fontSize: 12.5 }}>
                  {visibleSessions.length} из {sessions.length} сессий · web-chat · context algebra · abort · transfer
                </span>
              </div>
              <TableFilterBar
                activeFiltersLabel="Активные фильтры"
                className="sessions-filterbar"
                density="compact"
                fields={[
                  {
                    key: 'channel',
                    kind: 'select',
                    label: 'Канал',
                    options: CHANNEL_OPTIONS,
                    placeholder: 'Все каналы',
                    width: 172,
                  },
                  {
                    key: 'status',
                    kind: 'select',
                    label: 'Статус',
                    options: STATUS_OPTIONS,
                    placeholder: 'Все статусы',
                    width: 172,
                  },
                ]}
                onChange={(values) => {
                  setChannel(parseChannelFilter(values.channel))
                  setStatus(parseStatusFilter(values.status))
                }}
                onReset={() => {
                  setChannel('all')
                  setStatus('all')
                }}
                resetLabel="Сбросить"
                style={{
                  background: 'var(--frm-workbench-well, #0b1020)',
                  boxShadow: 'none',
                  marginLeft: 'auto',
                  padding: 10,
                }}
                values={filterValues}
              />
            </div>

            <div className="sessions-table" style={{ ...WELL_STYLE, marginTop: 18, overflowX: 'auto' }}>
              <div style={{ minWidth: 1390 }}>
                <div className="sessions-table-head" style={TABLE_HEAD_STYLE}>
                  <span>Канал</span>
                  <span>Агент</span>
                  <span>Проект</span>
                  <span>Спринт</span>
                  <span>Инициатор</span>
                  <span>Начало · активность</span>
                  <span>Статус</span>
                  <span>Контекст</span>
                  <span style={{ textAlign: 'right' }}>Действия</span>
                </div>
                {visibleSessions.map((session) => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    abortPending={abortMutation.isPending && abortMutation.variables === session.id}
                    contextExpanded={contextSessionId === session.id}
                    webChatPending={openWebChatMutation.isPending && openWebChatMutation.variables?.id === session.id}
                    onAbort={(id) => abortMutation.mutate(id)}
                    onOpenWebChat={() => openWebChatMutation.mutate(session)}
                    onToggleContext={() => setContextSessionId((current) => current === session.id ? null : session.id)}
                    onTransfer={() => openTransfer(session)}
                  />
                ))}
              </div>
            </div>

            {visibleSessions.length === 0 && (
              <EmptyState
                title="Нет активных сессий по фильтрам"
                description="Агенты ничего не выполняют и не ждут ввода в выбранном срезе."
                action={
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setChannel('all')
                      setStatus('all')
                    }}
                  >
                    Сбросить фильтры
                  </Button>
                }
                style={{ marginTop: 12 }}
              />
            )}
          </WorkbenchPanel>
        </>
      )}

      <TransferModal
        session={transferSession}
        targets={targetsQuery.data ?? []}
        targetsLoading={targetsQuery.isFetching}
        targetsError={targetsQuery.error}
        selectedTargetId={targetProjectId}
        diffReady={Boolean(
          transferSession &&
            targetProjectId &&
            diffReadyFor?.sessionId === transferSession.id &&
            diffReadyFor.targetProjectId === targetProjectId,
        )}
        diffPending={transferDiffMutation.isPending}
        confirmPending={transferConfirmMutation.isPending}
        onSelectTarget={(id) => {
          setTargetProjectId(id)
          setDiffReadyFor(null)
        }}
        onClose={closeTransfer}
        onRequestDiff={(sessionId, target) => transferDiffMutation.mutate({ id: sessionId, targetProjectId: target })}
        onConfirm={(sessionId, target) => transferConfirmMutation.mutate({ id: sessionId, targetProjectId: target })}
      />
    </div>
  )
}

function RegistryReadModelPanel({
  dto,
  records,
  filterValues,
  isLoading,
  isError,
  error,
  isFetching,
  onRefetch,
  onFilterChange,
  onResetFilters,
}: {
  dto?: ControlPlaneRegistryListDto
  records: ControlPlaneRegistryRecordDto[]
  filterValues: TableFilterValues
  isLoading: boolean
  isError: boolean
  error: unknown
  isFetching: boolean
  onRefetch: () => void
  onFilterChange: (values: TableFilterValues) => void
  onResetFilters: () => void
}) {
  const columns = useMemo<DataTableColumn<ControlPlaneRegistryRecordDto>[]>(
    () => [
      {
        key: 'channel',
        header: 'Канал',
        width: 126,
        render: (record) => <RegistryChannelCell record={record} />,
        sortable: true,
        sortAccessor: (record) => record.channel,
      },
      {
        key: 'agent',
        header: 'Агент',
        width: 184,
        render: (record) => <RegistryAgentCell record={record} />,
        sortable: true,
        sortAccessor: (record) => record.agentId,
      },
      {
        key: 'context',
        header: 'Проект · спринт',
        width: 270,
        render: (record) => <RegistryContextCell record={record} />,
        sortable: true,
        sortAccessor: (record) => `${record.project.id}:${record.sprint?.path ?? ''}`,
      },
      {
        key: 'task',
        header: 'Текущая работа',
        width: 320,
        render: (record) => <RegistryTaskCell record={record} />,
      },
      {
        key: 'activity',
        header: 'Активность · checkpoint',
        width: 230,
        render: (record) => <RegistryActivityCell record={record} />,
        sortable: true,
        sortAccessor: (record) => record.lastSeen,
      },
      {
        key: 'state',
        header: 'Статус · run',
        width: 176,
        render: (record) => <RegistryStateCell record={record} />,
        sortable: true,
        sortAccessor: (record) => record.status,
      },
      {
        key: 'source',
        header: 'Source',
        width: 168,
        render: (record) => <RegistrySourceCell record={record} />,
      },
    ],
    [],
  )

  const channelOptions = registryChannelOptions(dto)
  const statusOptions = registryStatusOptions(dto)
  const projectOptions = registryProjectOptions(dto)

  return (
    <WorkbenchPanel style={PANEL_STYLE}>
      <div style={TOOLBAR_STYLE}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <h2 style={{ fontSize: 16, fontWeight: 850, margin: 0 }}>Control-plane registry</h2>
          <span style={{ ...MUTED_STYLE, fontSize: 12.5 }}>
            {records.length} records в текущем срезе · read-only · без remote-control
          </span>
        </div>
        <TableFilterBar
          activeFiltersLabel="Фильтры registry"
          className="registry-filterbar"
          density="compact"
          fields={[
            {
              key: 'channel',
              kind: 'select',
              label: 'Канал',
              options: channelOptions,
              placeholder: 'Все каналы',
              width: 174,
            },
            {
              key: 'status',
              kind: 'select',
              label: 'Статус',
              options: statusOptions,
              placeholder: 'Все статусы',
              width: 174,
            },
            {
              key: 'project',
              kind: 'select',
              label: 'Проект',
              options: projectOptions,
              placeholder: 'Все проекты',
              width: 224,
            },
          ]}
          onChange={onFilterChange}
          onReset={onResetFilters}
          resetLabel="Сбросить"
          actions={
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Icon name="refresh" size={13} spinning={isFetching} />}
              onClick={onRefetch}
            >
              Refresh
            </Button>
          }
          style={{
            background: 'var(--frm-workbench-well, #0b1020)',
            boxShadow: 'none',
            marginLeft: 'auto',
            padding: 10,
          }}
          values={filterValues}
        />
      </div>

      {isLoading ? (
        <div style={{ ...STACK_STYLE, marginTop: 18 }}>
          <Skeleton height={44} />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
      ) : isError ? (
        <ErrorBanner
          title="Не удалось загрузить control-plane registry"
          description={error instanceof Error ? error.message : 'unknown error'}
          action={<Button onClick={onRefetch}>Повторить</Button>}
          style={{ marginTop: 18 }}
        />
      ) : records.length === 0 ? (
        <EmptyState
          title="Нет registry records по фильтрам"
          description="Снимите фильтры или дождитесь следующей runtime declaration."
          action={<Button size="sm" variant="secondary" onClick={onResetFilters}>Сбросить фильтры</Button>}
          style={{ marginTop: 18 }}
        />
      ) : (
        <div className="registry-table" style={{ marginTop: 18, overflowX: 'auto' }}>
          <DataTable
            rows={records}
            columns={columns}
            rowKey={(record) => record.id}
            density="compact"
            defaultSortKey="activity"
            defaultSortDir="desc"
            ariaLabel="Control-plane registry"
            emptyTitle="Нет registry records"
            style={{ minWidth: 1470 }}
          />
        </div>
      )}
    </WorkbenchPanel>
  )
}

function RegistryChannelCell({ record }: { record: ControlPlaneRegistryRecordDto }) {
  return (
    <div style={{ alignItems: 'center', display: 'flex', gap: 9, minWidth: 0 }}>
      <span style={registryChannelTileStyle(record.channel)}>{registryChannelAbbr(record.channel)}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <strong style={{ color: 'var(--frm-text-2)', fontSize: 12.5 }}>{registryChannelLabel(record.channel)}</strong>
        <span style={{ ...MONO_NUMERIC_STYLE, ...MUTED_STYLE, fontSize: 10.5 }}>{shortSessionId(record.id)}</span>
      </div>
    </div>
  )
}

function RegistryAgentCell({ record }: { record: ControlPlaneRegistryRecordDto }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <AgentLabel agent={record.agentId} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <Pill tone="neutral" size="sm">{record.role || 'role?'}</Pill>
        <Pill tone="info" size="sm">{record.engine || 'engine?'}</Pill>
      </div>
      <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {record.model || 'unknown'} · {record.mode || 'mode?'}
      </span>
    </div>
  )
}

function RegistryContextCell({ record }: { record: ControlPlaneRegistryRecordDto }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <ProjectPill project={record.project.id || 'unknown'} />
      {record.sprint ? (
        <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-accent-2)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {record.sprint.path}
        </span>
      ) : (
        <Pill tone="neutral" size="sm">no sprint</Pill>
      )}
      <code style={{ ...MONO_NUMERIC_STYLE, ...MUTED_STYLE, fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {record.project.cwd || 'cwd unresolved'}
      </code>
    </div>
  )
}

function RegistryTaskCell({ record }: { record: ControlPlaneRegistryRecordDto }) {
  const task = record.currentTask
  const claims = record.claimedFiles.slice(0, 3)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      {task ? (
        <>
          <strong style={{ color: 'var(--frm-text)', fontSize: 13, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {task.title}
          </strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Pill tone="info" size="sm">{task.source}</Pill>
            {task.sourceRef && <Pill tone="neutral" size="sm">{task.sourceRef}</Pill>}
          </div>
        </>
      ) : (
        <span style={{ ...MUTED_STYLE, fontSize: 12.5 }}>нет current task</span>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {claims.length ? claims.map((claim) => (
          <Pill key={`${claim.access}:${claim.path}`} tone={claim.access === 'write' ? 'warning' : claim.access === 'read' ? 'neutral' : 'info'} size="sm">
            {claim.access}: {claim.path}
          </Pill>
        )) : <Pill tone="neutral" size="sm">no claimed files</Pill>}
        {record.claimedFiles.length > claims.length && (
          <Pill tone="neutral" size="sm">+{record.claimedFiles.length - claims.length}</Pill>
        )}
      </div>
    </div>
  )
}

function RegistryActivityCell({ record }: { record: ControlPlaneRegistryRecordDto }) {
  const checkpointPath = record.lastCheckpoint?.chatPath ?? record.lastCheckpoint?.roadmapPath ?? ''
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={{ alignItems: 'center', color: freshnessColor(record.lastSeen), display: 'inline-flex', fontSize: 11.5, gap: 6 }}>
        <Icon name="clock" size={11} />
        seen {formatAge(record.lastSeen)}
      </span>
      <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', fontSize: 11 }}>
        activity {record.lastActivityAt ? formatAge(record.lastActivityAt) : '—'}
      </span>
      {checkpointPath ? (
        <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-2)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {checkpointPath}
        </code>
      ) : (
        <Pill tone="neutral" size="sm">no checkpoint</Pill>
      )}
    </div>
  )
}

function RegistryStateCell({ record }: { record: ControlPlaneRegistryRecordDto }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <StatusPill status={registryStatusKind(record.status)} label={record.status} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <Pill tone={record.runState.state === 'running' ? 'success' : record.runState.state === 'blocked' ? 'warning' : 'neutral'} size="sm">
          run: {record.runState.state}
        </Pill>
        <Pill tone={record.runState.cancellable ? 'warning' : 'neutral'} size="sm">
          {record.runState.cancellable ? 'cancellable' : 'read-only'}
        </Pill>
      </div>
      {record.handoff && (
        <Pill tone="info" size="sm">{record.handoff.state}</Pill>
      )}
    </div>
  )
}

function RegistrySourceCell({ record }: { record: ControlPlaneRegistryRecordDto }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <Pill tone="neutral" size="sm">runtime {record.source.runtime.length}</Pill>
      <Pill tone="neutral" size="sm">vault {record.source.vault.length}</Pill>
      <Pill tone="neutral" size="sm">derived {record.source.derived.length}</Pill>
      {record.source.runtime[0] && (
        <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-3)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {record.source.runtime[0]}
        </code>
      )}
    </div>
  )
}

function PendingPermissions({
  sessions,
  pendingMutationId,
  pendingDecision,
  isPending,
  onDecision,
}: {
  sessions: OwnerSessionDto[]
  pendingMutationId?: string
  pendingDecision?: PermissionDecision
  isPending: boolean
  onDecision: (id: string, decision: PermissionDecision) => void
}) {
  return (
    <WorkbenchPanel
      style={{
        ...PANEL_STYLE,
        background: 'linear-gradient(180deg, rgba(251,191,36,0.07), rgba(16,20,35,0.6)), var(--frm-workbench-panel)',
        borderColor: 'rgba(251, 191, 36, 0.34)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
        <span
          aria-hidden
          style={{
            alignItems: 'center',
            background: 'rgba(251, 191, 36, 0.14)',
            border: '1px solid rgba(251, 191, 36, 0.34)',
            borderRadius: 9,
            color: 'var(--frm-warning)',
            display: 'inline-flex',
            height: 30,
            justifyContent: 'center',
            width: 30,
          }}
        >
          <Icon name="warning" />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <h2 style={{ fontSize: 16, fontWeight: 850, margin: 0 }}>Запросы дополнительных прав</h2>
          <span style={{ ...MUTED_STYLE, fontSize: 12.5 }}>
            агент приостановил выполнение и ждёт решения владельца ·{' '}
            <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-warning)' }}>{sessions.length}</span>
          </span>
        </div>
      </div>

      <div style={STACK_STYLE}>
        {sessions.map((session) => (
          <Surface
            key={session.id}
            padding="md"
            style={{
              background: 'var(--frm-workbench-well)',
              borderColor: 'rgba(251, 191, 36, 0.26)',
            }}
          >
            <div className="sessions-permission-card" style={{ alignItems: 'flex-start', display: 'flex', gap: 15, justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0 }}>
                <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-muted)', fontSize: 12 }}>{shortSessionId(session.id)}</span>
                  <AgentLabel agent={session.agent} />
                  <ProjectPill project={session.project} />
                  <Pill tone="warning">{session.permission?.risk ?? 'risk'}</Pill>
                </div>
                <strong style={{ color: 'var(--frm-text)', fontSize: 14, lineHeight: 1.45 }}>
                  {session.permission?.action ?? 'Требуется подтверждение'}
                </strong>
                <code style={{ ...pathChipStyle(), whiteSpace: 'normal' }}>{session.permission?.path ?? 'unknown path'}</code>
              </div>
              <div className="sessions-actions" style={{ display: 'flex', flexShrink: 0, gap: 9 }}>
                <Button
                  size="sm"
                  variant="danger"
                  loading={isPending && pendingMutationId === session.id && pendingDecision === 'deny'}
                  onClick={() => onDecision(session.id, 'deny')}
                >
                  Отказать
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={isPending && pendingMutationId === session.id && pendingDecision === 'allow'}
                  onClick={() => onDecision(session.id, 'allow')}
                >
                  Разрешить
                </Button>
              </div>
            </div>
          </Surface>
        ))}
      </div>
    </WorkbenchPanel>
  )
}

function SessionRow({
  session,
  abortPending,
  contextExpanded,
  webChatPending,
  onAbort,
  onOpenWebChat,
  onToggleContext,
  onTransfer,
}: {
  session: OwnerSessionDto
  abortPending: boolean
  contextExpanded: boolean
  webChatPending: boolean
  onAbort: (id: string) => void
  onOpenWebChat: () => void
  onToggleContext: () => void
  onTransfer: () => void
}) {
  const viewStatus = getViewStatus(session)
  const href = sessionHref(session)
  const canAbort = session.status === 'running'

  return (
    <>
      <article
        className="sessions-row"
        style={{
          ...ROW_STYLE,
          background: session.status === 'running' ? 'rgba(52, 211, 153, 0.025)' : 'transparent',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', gap: 9, minWidth: 0 }}>
          <span style={channelTileStyle(session.channel)}>
            <Icon name={session.channel === 'telegram' ? 'telegram' : 'web'} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <strong style={{ color: 'var(--frm-text-2)', fontSize: 12.5 }}>{session.channel}</strong>
            <span style={{ ...MONO_NUMERIC_STYLE, ...MUTED_STYLE, fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {session.chatId}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <AgentLabel agent={session.agent} />
          {session.task && (
            <span style={{ alignItems: 'center', color: 'var(--frm-accent)', display: 'inline-flex', fontSize: 11.5, gap: 7, minWidth: 0 }}>
              <Icon name="play" size={11} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.task}</span>
            </span>
          )}
        </div>

        <div style={{ minWidth: 0 }}>
          <ProjectPill project={session.project} />
        </div>

        <SprintCell session={session} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text-2)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {session.initiator}
          </span>
          <Pill tone={session.initKind === 'sso' ? 'info' : 'neutral'} size="sm">
            {session.initKind === 'sso' ? 'SSO · owner' : 'telegram'}
          </Pill>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ alignItems: 'center', color: 'var(--frm-text-3)', display: 'inline-flex', fontSize: 11.5, gap: 6 }}>
            <Icon name="clock" size={11} />
            {formatAge(session.startedAt)}
          </span>
          <span style={{ ...MONO_NUMERIC_STYLE, color: freshnessColor(session.lastActivityAt), fontSize: 11.5 }}>
            {formatAge(session.lastActivityAt)}
          </span>
        </div>

        <div>
          <StatusPill status={statusKind(viewStatus)} label={statusLabel(viewStatus)} />
        </div>

        <div>
          <Button
            size="sm"
            variant={contextExpanded ? 'secondary' : 'ghost'}
            onClick={onToggleContext}
            style={{ minHeight: 30, paddingInline: 10 }}
          >
            Алгебра
          </Button>
        </div>

        <div className="sessions-actions" style={{ alignItems: 'center', display: 'flex', flexWrap: 'nowrap', gap: 8, justifyContent: 'flex-end' }}>
          <Tooltip content="Перейти в web-chat с тем же агентом, проектом и спринтом">
            <IconButton
              icon={webChatPending ? <Icon name="refresh" size={15} spinning /> : <Icon name="chat" size={15} />}
              label="В web чат"
              size="md"
              variant="secondary"
              disabled={webChatPending}
              onClick={onOpenWebChat}
              style={webChatActionStyle()}
            />
          </Tooltip>
          <Tooltip content={session.channel === 'telegram' ? 'Открыть исходный Telegram-чат' : 'Открыть исходную web-сессию'}>
            <a
              aria-label={session.channel === 'telegram' ? 'Открыть исходный Telegram-чат' : 'Открыть исходную web-сессию'}
              href={href.href}
              target={href.target}
              rel={href.target === '_blank' ? 'noreferrer' : undefined}
              style={sourceActionLinkStyle()}
              title={session.channel === 'telegram' ? 'Открыть исходный Telegram-чат' : 'Открыть исходную web-сессию'}
            >
              <Icon name={session.channel === 'telegram' ? 'telegram' : 'web'} size={15} />
            </a>
          </Tooltip>
          <Tooltip content={canAbort ? 'Прервать текущую задачу' : 'Abort доступен только для running-сессий'}>
            <IconButton
              icon={abortPending ? <Icon name="refresh" size={15} spinning /> : <Icon name="stop" size={15} />}
              label="Прервать текущую задачу"
              size="md"
              variant={canAbort ? 'danger' : 'ghost'}
              disabled={!canAbort || abortPending}
              onClick={() => onAbort(session.id)}
              style={sessionActionIconStyle(canAbort ? 'danger' : 'neutral')}
            />
          </Tooltip>
          <Tooltip content="Перенести сессию">
            <IconButton
              icon={<Icon name="move" size={15} />}
              label="Перенести сессию"
              size="md"
              variant="ghost"
              onClick={onTransfer}
              style={sessionActionIconStyle('neutral')}
            />
          </Tooltip>
        </div>
      </article>
      {contextExpanded && <ContextAlgebraPanel session={session} />}
    </>
  )
}

function SprintCell({ session }: { session: OwnerSessionDto }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      {session.sprint ? (
        <span style={{ ...MONO_NUMERIC_STYLE, alignItems: 'center', color: 'var(--frm-accent-2)', display: 'inline-flex', fontSize: 11.5, gap: 6, minWidth: 0 }}>
          <span style={{ background: 'var(--frm-accent-2)', borderRadius: '50%', flexShrink: 0, height: 5, width: 5 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{session.sprint}</span>
        </span>
      ) : (
        <Pill tone="neutral" size="sm">no sprint</Pill>
      )}
      <span style={{ ...MONO_NUMERIC_STYLE, ...MUTED_STYLE, fontSize: 10.5 }}>
        {session.sprint ? 'sprint.context' : 'context.subproject'}
      </span>
    </div>
  )
}

function ContextAlgebraPanel({ session }: { session: OwnerSessionDto }) {
  const rows = contextAlgebraRows(session)

  return (
    <div
      className="sessions-context-row"
      style={{
        background: 'linear-gradient(180deg, rgba(56,189,248,0.045), rgba(8,12,22,0.34)), var(--frm-workbench-well)',
        borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
        padding: '16px 18px 18px',
      }}
    >
      <div style={{ alignItems: 'flex-start', display: 'flex', gap: 12, justifyContent: 'space-between', marginBottom: 13 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <strong style={{ color: 'var(--frm-text)', fontSize: 13.5 }}>Алгебра контекста сессии</strong>
          <span style={{ color: 'var(--frm-text-3)', fontSize: 12 }}>
            Без сырого prompt и секретов: показываем только слои, источники и текущую привязку.
          </span>
        </div>
        <Pill tone="info" size="sm">{shortSessionId(session.id)}</Pill>
      </div>

      <div className="sessions-context-grid" style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        {rows.map((row) => (
          <Surface
            key={row.id}
            padding="sm"
            style={{
              background: row.current ? 'rgba(56, 189, 248, 0.08)' : 'var(--frm-workbench-well-2, #080c16)',
              borderColor: row.current ? 'rgba(56, 189, 248, 0.32)' : 'var(--frm-line-subtle)',
              minWidth: 0,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
              <div style={{ alignItems: 'center', display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                <span style={{ ...MONO_NUMERIC_STYLE, color: row.current ? 'var(--frm-accent)' : 'var(--frm-text-2)', fontSize: 11.5, fontWeight: 800 }}>
                  {row.label}
                </span>
                {row.current && <Pill tone="info" size="sm">current</Pill>}
              </div>
              <span style={{ color: 'var(--frm-text-3)', fontSize: 12, lineHeight: 1.45 }}>{row.description}</span>
              <code style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.source}
              </code>
            </div>
          </Surface>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        <Pill tone="neutral" size="sm">agent_id: {session.agent}</Pill>
        <Pill tone="neutral" size="sm">channel: {session.channel}</Pill>
        <Pill tone={session.task ? 'info' : 'neutral'} size="sm">
          request: {session.task ? 'active task' : 'next user message'}
        </Pill>
      </div>
    </div>
  )
}

function TransferModal({
  session,
  targets,
  targetsLoading,
  targetsError,
  selectedTargetId,
  diffReady,
  diffPending,
  confirmPending,
  onSelectTarget,
  onClose,
  onRequestDiff,
  onConfirm,
}: {
  session: OwnerSessionDto | null
  targets: SessionTransferTarget[]
  targetsLoading: boolean
  targetsError: unknown
  selectedTargetId: string
  diffReady: boolean
  diffPending: boolean
  confirmPending: boolean
  onSelectTarget: (id: string) => void
  onClose: () => void
  onRequestDiff: (sessionId: string, targetProjectId: string) => void
  onConfirm: (sessionId: string, targetProjectId: string) => void
}) {
  const selectedTarget = targets.find((target) => target.id === selectedTargetId) ?? null
  const canRequestDiff = Boolean(session && selectedTarget && !selectedTarget.disabled)
  const canConfirm = canRequestDiff && diffReady

  return (
    <Modal
      open={Boolean(session)}
      onClose={onClose}
      title="Перенос сессии в другой проект"
      description={session ? `${shortSessionId(session.id)} · ${session.agent} · ${session.channel}` : undefined}
      size="lg"
      style={{ maxWidth: 920, background: 'var(--frm-workbench-panel)' }}
      footer={
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between' }}>
          <span style={{ ...MONO_NUMERIC_STYLE, ...MUTED_STYLE, alignItems: 'center', display: 'inline-flex', fontSize: 11, gap: 8 }}>
            <span style={{ background: 'var(--frm-text-muted)', borderRadius: '50%', height: 6, width: 6 }} />
            {'edit -> diff -> confirm · запись cwd + restart'}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <Button variant="ghost" onClick={onClose}>Отмена</Button>
            <Button
              variant="secondary"
              disabled={!canRequestDiff}
              loading={diffPending}
              onClick={() => session && selectedTarget && onRequestDiff(session.id, selectedTarget.id)}
            >
              Сравнить
            </Button>
            <Button
              variant="primary"
              disabled={!canConfirm}
              loading={confirmPending}
              onClick={() => session && selectedTarget && onConfirm(session.id, selectedTarget.id)}
              title={canConfirm ? 'Подтвердить перенос' : 'Подтверждение включится после успешного backend diff'}
            >
              Перенести и перезапустить
            </Button>
          </div>
        </div>
      }
    >
      {!session ? null : (
        <div className="sessions-modal-grid" style={{ display: 'grid', gap: 18, gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
          <div style={STACK_STYLE}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <strong style={{ fontSize: 13 }}>Проект назначения</strong>
              <span style={{ ...MUTED_STYLE, fontSize: 11.5 }}>
                {session.channel === 'telegram'
                  ? 'Канал telegram ограничивается GROUP_PROJECTS, если backend знает группу.'
                  : 'Канал web (owner) видит активные и планируемые проекты.'}
              </span>
            </div>

            {targetsLoading ? (
              <div style={STACK_STYLE}>
                <Skeleton height={58} />
                <Skeleton height={58} />
                <Skeleton height={58} />
              </div>
            ) : targetsError ? (
              <ErrorBanner
                title="Не удалось загрузить цели переноса"
                description={targetsError instanceof Error ? targetsError.message : 'unknown error'}
              />
            ) : (
              <div className="frm-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto', paddingRight: 2 }}>
                {targets.map((target) => (
                  <button
                    key={target.id}
                    disabled={target.disabled}
                    onClick={() => onSelectTarget(target.id)}
                    style={targetButtonStyle(target, target.id === selectedTargetId)}
                    type="button"
                  >
                    <ProjectTile project={target.id} size={34} disabled={target.disabled} />
                    <span style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 3, minWidth: 0, textAlign: 'left' }}>
                      <span style={{ ...MONO_NUMERIC_STYLE, color: 'var(--frm-text)', fontSize: 13, fontWeight: 650 }}>{target.name}</span>
                      <span style={{ color: 'var(--frm-text-3)', fontSize: 11.5 }}>
                        {target.sprint ? `${target.sprint.id} · ${target.status}` : `${target.status} · без активного спринта`}
                      </span>
                    </span>
                    {target.disabled && <Pill tone="neutral">текущий</Pill>}
                    {target.id === selectedTargetId && <Icon name="check" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={STACK_STYLE}>
            <strong style={{ fontSize: 13 }}>Что изменится</strong>
            {!selectedTarget ? (
              <EmptyState
                title="Выберите проект"
                description="После выбора появится client preview cwd/env. Реальный diff запрашивается отдельной кнопкой."
                style={{ minHeight: 220 }}
              />
            ) : (
              <>
                <DiffViewer
                  compact
                  title="Client preview"
                  description="Визуальная схема изменения; source of truth для записи — backend diff endpoint."
                  lines={transferPreviewLines(session, selectedTarget)}
                />
                <Surface
                  padding="md"
                  style={{
                    background: session.status === 'running' ? 'rgba(251, 191, 36, 0.06)' : 'var(--frm-workbench-well)',
                    borderColor: session.status === 'running' ? 'rgba(251, 191, 36, 0.30)' : 'var(--frm-line-subtle)',
                  }}
                >
                  <span style={{ color: session.status === 'running' ? 'var(--frm-warning)' : 'var(--frm-text-3)', fontSize: 12.5, lineHeight: 1.5 }}>
                    {session.status === 'running'
                      ? 'Running-сессия перезапустится: SIGTERM -> respawn с новым cwd.'
                      : 'Сессия не выполняет задачу; перенос не должен прерывать активный engine-run.'}
                  </span>
                </Surface>
                {!diffReady && (
                  <Pill tone="info" style={{ alignSelf: 'flex-start' }}>
                    confirm заблокирован до backend diff
                  </Pill>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

function SessionsSkeleton() {
  return (
    <WorkbenchPanel style={PANEL_STYLE}>
      <div style={STACK_STYLE}>
        <Skeleton height={36} width={260} />
        <Skeleton height={52} />
        <Skeleton height={52} />
        <Skeleton height={52} />
      </div>
    </WorkbenchPanel>
  )
}

function AgentLabel({ agent }: { agent: string }) {
  return (
    <span style={{ ...MONO_NUMERIC_STYLE, alignItems: 'center', color: 'var(--frm-text)', display: 'inline-flex', fontSize: 13, fontWeight: 650, gap: 7 }}>
      <span style={{ background: agentColor(agent), borderRadius: '50%', flexShrink: 0, height: 7, width: 7 }} />
      {agent}
    </span>
  )
}

function ProjectPill({ project }: { project: string }) {
  return (
    <span
      style={{
        ...MONO_NUMERIC_STYLE,
        alignItems: 'center',
        background: 'var(--frm-workbench-well, #0B1020)',
        border: '1px solid var(--frm-line-subtle)',
        borderRadius: 999,
        color: 'var(--frm-text-2)',
        display: 'inline-flex',
        fontSize: 11.5,
        fontWeight: 650,
        gap: 7,
        minHeight: 24,
        padding: '0 10px 0 4px',
      }}
    >
      <ProjectTile project={project} size={18} />
      {project || 'default'}
    </span>
  )
}

function ProjectTile({ project, size, disabled = false }: { project: string; size: number; disabled?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        alignItems: 'center',
        background: projectGradient(project),
        borderRadius: Math.max(5, Math.round(size / 4)),
        color: '#04111d',
        display: 'inline-flex',
        flexShrink: 0,
        fontSize: size <= 20 ? 8.5 : 12,
        fontWeight: 850,
        height: size,
        justifyContent: 'center',
        opacity: disabled ? 0.42 : 1,
        width: size,
      }}
    >
      {projectAbbr(project)}
    </span>
  )
}

function Icon({
  name,
  size = 15,
  spinning = false,
}: {
  name: 'refresh' | 'warning' | 'telegram' | 'web' | 'play' | 'clock' | 'chat' | 'move' | 'check' | 'stop'
  size?: number
  spinning?: boolean
}) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  }
  return (
    <svg
      aria-hidden
      height={size}
      viewBox="0 0 24 24"
      width={size}
      style={{ animation: spinning ? 'frm-spin 800ms linear infinite' : undefined, flexShrink: 0 }}
      {...common}
    >
      {name === 'refresh' && (
        <>
          <path d="M21 12a9 9 0 0 1-15.2 6.5" />
          <path d="M3 12A9 9 0 0 1 18.2 5.5" />
          <path d="M18 2v4h-4" />
          <path d="M6 22v-4h4" />
        </>
      )}
      {name === 'warning' && (
        <>
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        </>
      )}
      {name === 'telegram' && (
        <>
          <path d="m22 3-9.5 9.5" />
          <path d="M22 3 15 21l-4-8-8-4 19-6Z" />
        </>
      )}
      {name === 'web' && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
        </>
      )}
      {name === 'play' && <path d="m8 5 11 7-11 7V5Z" fill="currentColor" stroke="none" />}
      {name === 'clock' && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </>
      )}
      {name === 'chat' && <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />}
      {name === 'move' && (
        <>
          <path d="M5 12h14" />
          <path d="m13 6 6 6-6 6" />
          <path d="M5 6v12" opacity=".5" />
        </>
      )}
      {name === 'check' && (
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="m8 12 3 3 5-6" />
        </>
      )}
      {name === 'stop' && <rect x="6" y="6" width="12" height="12" rx="2" />}
    </svg>
  )
}

function matchesFilters(session: OwnerSessionDto, channel: ChannelFilter, status: StatusFilter) {
  if (channel !== 'all' && session.channel !== channel) return false
  if (status === 'all') return true
  if (status === 'permission') return PERMISSION_FLOW_ENABLED && Boolean(session.permission)
  return session.status === status
}

function matchesRegistryFilters(
  record: ControlPlaneRegistryRecordDto,
  channel: RegistryChannelFilter,
  status: RegistryStatusFilter,
  project: RegistryProjectFilter,
) {
  if (channel !== 'all' && record.channel !== channel) return false
  if (status !== 'all' && record.status !== status) return false
  if (project !== 'all' && record.project.id !== project) return false
  return true
}

function parseRegistryChannelFilter(value: unknown): RegistryChannelFilter {
  return typeof value === 'string' && REGISTRY_CHANNELS.includes(value as ControlPlaneChannel)
    ? value as ControlPlaneChannel
    : 'all'
}

function parseRegistryStatusFilter(value: unknown): RegistryStatusFilter {
  return typeof value === 'string' && REGISTRY_STATUSES.includes(value as ControlPlaneStatus)
    ? value as ControlPlaneStatus
    : 'all'
}

function parseRegistryProjectFilter(value: unknown): RegistryProjectFilter {
  return typeof value === 'string' && value ? value : 'all'
}

function parseChannelFilter(value: unknown): ChannelFilter {
  return value === 'telegram' || value === 'web' ? value : 'all'
}

function parseStatusFilter(value: unknown): StatusFilter {
  return value === 'running' ||
    value === 'idle' ||
    value === 'aborted' ||
    value === 'error' ||
    (PERMISSION_FLOW_ENABLED && value === 'permission')
    ? value
    : 'all'
}

function summarizeRegistry(records: ControlPlaneRegistryRecordDto[]) {
  return {
    total: records.length,
    running: records.filter((record) => record.status === 'running').length,
    blocked: records.filter((record) => record.status === 'blocked' || record.status === 'waiting_permission').length,
    cli: records.filter((record) => record.channel === 'cursor-cli' || record.channel === 'codex-desktop' || record.channel === 'manual').length,
    projects: new Set(records.map((record) => record.project.id).filter(Boolean)).size,
  }
}

function registryChannelOptions(dto?: ControlPlaneRegistryListDto) {
  const channels = dto?.filters.channels.length ? dto.filters.channels : REGISTRY_CHANNELS
  return channels.map((channel) => ({ value: channel, label: registryChannelLabel(channel) }))
}

function registryStatusOptions(dto?: ControlPlaneRegistryListDto) {
  const statuses = dto?.filters.statuses.length ? dto.filters.statuses : REGISTRY_STATUSES
  return statuses.map((status) => ({ value: status, label: status }))
}

function registryProjectOptions(dto?: ControlPlaneRegistryListDto) {
  const projects = dto?.filters.projects ?? []
  return projects.map((project) => ({ value: project.id, label: project.title === project.id ? project.id : `${project.title} · ${project.id}` }))
}

function registryChannelLabel(channel: ControlPlaneChannel) {
  if (channel === 'telegram') return 'Telegram'
  if (channel === 'web') return 'Web'
  if (channel === 'cursor-cli') return 'Cursor CLI'
  if (channel === 'codex-desktop') return 'Codex Desktop'
  return 'Manual'
}

function registryChannelAbbr(channel: ControlPlaneChannel) {
  if (channel === 'telegram') return 'TG'
  if (channel === 'web') return 'WB'
  if (channel === 'cursor-cli') return 'CR'
  if (channel === 'codex-desktop') return 'CD'
  return 'MN'
}

function registryChannelTileStyle(channel: ControlPlaneChannel): CSSProperties {
  const colors: Record<ControlPlaneChannel, { background: string; border: string; color: string }> = {
    telegram: {
      background: 'rgba(56, 189, 248, 0.10)',
      border: 'rgba(56, 189, 248, 0.24)',
      color: 'var(--frm-accent)',
    },
    web: {
      background: 'rgba(129, 140, 248, 0.10)',
      border: 'rgba(129, 140, 248, 0.24)',
      color: 'var(--frm-accent-2)',
    },
    'cursor-cli': {
      background: 'rgba(52, 211, 153, 0.10)',
      border: 'rgba(52, 211, 153, 0.24)',
      color: 'var(--frm-success)',
    },
    'codex-desktop': {
      background: 'rgba(251, 191, 36, 0.10)',
      border: 'rgba(251, 191, 36, 0.28)',
      color: 'var(--frm-warning)',
    },
    manual: {
      background: 'rgba(148, 163, 184, 0.10)',
      border: 'rgba(148, 163, 184, 0.24)',
      color: 'var(--frm-text-2)',
    },
  }
  const palette = colors[channel]

  return {
    ...MONO_NUMERIC_STYLE,
    alignItems: 'center',
    background: palette.background,
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    color: palette.color,
    display: 'inline-flex',
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 850,
    height: 30,
    justifyContent: 'center',
    width: 34,
  }
}

function registryStatusKind(status: ControlPlaneStatus) {
  if (status === 'running' || status === 'closed') return 'ok' as const
  if (status === 'idle') return 'unknown' as const
  if (status === 'waiting_permission' || status === 'blocked' || status === 'aborted' || status === 'stale') return 'warning' as const
  return 'danger' as const
}

function getViewStatus(session: OwnerSessionDto): ViewStatus {
  if (PERMISSION_FLOW_ENABLED && session.permission) return 'permission'
  return session.status
}

function statusKind(status: ViewStatus) {
  if (status === 'running') return 'ok' as const
  if (status === 'idle') return 'unknown' as const
  if (status === 'aborted' || status === 'permission') return 'warning' as const
  return 'danger' as const
}

function statusLabel(status: ViewStatus) {
  if (status === 'permission') return 'permission'
  return status
}

function freshnessColor(value: string) {
  const ts = Date.parse(value)
  if (!Number.isFinite(ts)) return 'var(--frm-text-muted)'
  const ageMin = Math.max(0, (Date.now() - ts) / 60_000)
  if (ageMin <= 5) return 'var(--frm-success)'
  if (ageMin <= 60) return 'var(--frm-warning)'
  return 'var(--frm-text-muted)'
}

function formatDateTime(value: string) {
  const ts = Date.parse(value)
  if (!Number.isFinite(ts)) return value
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
  }).format(new Date(ts))
}

function shortSessionId(id: string) {
  if (id.length <= 12) return id
  return `${id.slice(0, 6)}...${id.slice(-4)}`
}

function sessionHref(session: OwnerSessionDto): { href: string; target?: string } {
  if (session.channel === 'web') {
    return { href: `/chat/${encodeURIComponent(session.agent)}?session=${encodeURIComponent(session.id)}` }
  }
  const chatId = session.chatId.split(':')[0]?.replace(/^chat\s+/, '') ?? ''
  if (chatId.startsWith('-100')) {
    return { href: `https://t.me/c/${chatId.slice(4)}`, target: '_blank' }
  }
  return { href: 'https://t.me/', target: '_blank' }
}

function contextAlgebraRows(session: OwnerSessionDto) {
  const project = session.project || 'default'
  const sprint = session.sprint || 'no sprint'

  return [
    {
      id: 'skills',
      label: 'skills',
      description: 'Платформенные common/role skills и проектные skills текущего узла.',
      source: `repo/ai.agents-platform/skills + ${project}/skills`,
    },
    {
      id: 'subproject',
      label: 'context.subproject',
      description: 'Надпроекты, context.md проекта, docs/ и карты product/design/devops.',
      source: `frm.ai/context.md -> ${project}/context.md`,
      current: true,
    },
    {
      id: 'sprint',
      label: 'sprint.context',
      description: session.sprint
        ? 'Roadmap текущего спринта добавляется поверх project context.'
        : 'Спринт не выбран: остаётся context.subproject без sprint layer.',
      source: session.sprint ? `${project}/sprints/${sprint}/roadmap.md` : `${project}/context.md`,
      current: Boolean(session.sprint),
    },
    {
      id: 'history',
      label: 'session history',
      description: 'История transport/web-session, channel/chat id, агент и последние события.',
      source: `${session.channel}:${session.chatId} · ${session.agent}`,
      current: true,
    },
    {
      id: 'request',
      label: 'current request',
      description: 'Текущая running-задача или следующий пользовательский запрос.',
      source: session.task || 'ожидает новое сообщение',
      current: Boolean(session.task),
    },
  ]
}

function channelTileStyle(channel: OwnerSessionDto['channel']): CSSProperties {
  const isTelegram = channel === 'telegram'
  return {
    alignItems: 'center',
    background: isTelegram ? 'rgba(56, 189, 248, 0.10)' : 'rgba(129, 140, 248, 0.10)',
    border: `1px solid ${isTelegram ? 'rgba(56, 189, 248, 0.24)' : 'rgba(129, 140, 248, 0.24)'}`,
    borderRadius: 8,
    color: isTelegram ? 'var(--frm-accent)' : 'var(--frm-accent-2)',
    display: 'inline-flex',
    flexShrink: 0,
    height: 30,
    justifyContent: 'center',
    width: 30,
  }
}

function webChatActionStyle(): CSSProperties {
  return {
    background: 'linear-gradient(135deg, var(--frm-accent), var(--frm-accent-2))',
    border: '0',
    boxShadow: '0 8px 20px rgba(56, 189, 248, 0.18)',
    color: 'var(--frm-text-on-accent)',
  }
}

function sourceActionLinkStyle(): CSSProperties {
  return {
    alignItems: 'center',
    background: 'transparent',
    border: '1px solid var(--frm-line-subtle, rgba(148,163,184,0.14))',
    borderRadius: 8,
    color: 'var(--frm-text-2)',
    display: 'inline-flex',
    height: 34,
    justifyContent: 'center',
    textDecoration: 'none',
    width: 34,
  }
}

function sessionActionIconStyle(tone: 'danger' | 'neutral'): CSSProperties {
  return {
    background: 'transparent',
    border: `1px solid ${tone === 'danger' ? 'rgba(251, 113, 133, 0.45)' : 'var(--frm-line-subtle, rgba(148,163,184,0.14))'}`,
    color: tone === 'danger' ? 'var(--frm-danger)' : 'var(--frm-text-2)',
  }
}

function pathChipStyle(): CSSProperties {
  return {
    ...MONO_NUMERIC_STYLE,
    alignItems: 'center',
    alignSelf: 'flex-start',
    background: 'var(--frm-workbench-well-2, #080c16)',
    border: '1px solid var(--frm-line-subtle)',
    borderRadius: 9,
    color: 'var(--frm-text-2)',
    display: 'inline-flex',
    fontSize: 12,
    gap: 8,
    padding: '7px 11px',
  }
}

function targetButtonStyle(target: SessionTransferTarget, selected: boolean): CSSProperties {
  return {
    alignItems: 'center',
    background: selected ? 'rgba(56, 189, 248, 0.10)' : 'var(--frm-workbench-well)',
    border: `1px solid ${selected ? 'rgba(56, 189, 248, 0.45)' : 'var(--frm-line-subtle)'}`,
    borderRadius: 12,
    color: 'inherit',
    cursor: target.disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    fontFamily: 'var(--frm-font-ui)',
    gap: 12,
    opacity: target.disabled ? 0.62 : 1,
    padding: '12px 13px',
    width: '100%',
  }
}

function transferPreviewLines(session: OwnerSessionDto, target: SessionTransferTarget) {
  return [
    { kind: 'removed' as const, content: `cwd=${contextPath(session.project)}` },
    { kind: 'added' as const, content: `cwd=${contextPath(target.id)}` },
    { kind: 'removed' as const, content: `AGENT_PROJECT=${session.project || 'default'}` },
    { kind: 'added' as const, content: `AGENT_PROJECT=${target.id}` },
    { kind: 'removed' as const, content: `AGENT_SPRINT=${session.sprint || '-'}` },
    { kind: 'added' as const, content: `AGENT_SPRINT=${target.sprint?.id || '- (нет)'}` },
    { kind: 'context' as const, content: `${target.id}/context.md будет перечитан при следующей сборке context.session` },
  ]
}

function contextPath(project: string) {
  return `vault/${project || 'default'}`
}

function projectAbbr(project: string) {
  if (PROJECT_ABBR[project]) return PROJECT_ABBR[project]
  const last = project.split('/').filter(Boolean).pop() ?? project
  return last.slice(0, 2).toUpperCase()
}

function projectGradient(project: string) {
  const hash = project.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
  const pair = PROJECT_GRADIENTS[hash % PROJECT_GRADIENTS.length]
  return `linear-gradient(135deg, ${pair?.[0] ?? '#38BDF8'}, ${pair?.[1] ?? '#6366F1'})`
}

function agentColor(agent: string) {
  if (agent.includes('devops')) return 'var(--frm-success)'
  if (agent.includes('comm')) return 'var(--frm-accent-2)'
  if (agent.includes('fin')) return 'var(--frm-warning)'
  return 'var(--frm-accent)'
}
